import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Mail,
  ArrowLeft,
  CheckCircle,
  Upload,
  ExternalLink,
  MousePointerClick,
  Camera,
  Share2,
  AlertCircle,
  Clock,
  Play,
  Timer,
  Plus
} from "lucide-react";

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [activeInstance, setActiveInstance] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [registeredEmails, setRegisteredEmails] = useState([]);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Email task types that require registered emails
  const EMAIL_TASK_TYPES = ["reply_email", "email_task", "forward_email"];

  useEffect(() => {
    fetchTask();
    fetchEmails();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [taskId]);

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/v2/users/emails`, { headers });
      setRegisteredEmails(response.data || []);
    } catch (error) {
      console.error("Error fetching emails:", error);
    }
  };

  useEffect(() => {
    // Start countdown timer if there's an active instance
    if (activeInstance && activeInstance.remaining_seconds > 0) {
      setRemainingTime(activeInstance.remaining_seconds);
      
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            toast.error("Time expired! Task has been cancelled.");
            setActiveInstance(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeInstance]);

  const fetchTask = async () => {
    try {
      const [taskRes, submissionsRes, instanceRes] = await Promise.all([
        axios.get(`${API}/v2/tasks/${taskId}`, { headers }),
        axios.get(`${API}/v2/submissions`, { headers }),
        axios.get(`${API}/v2/tasks/${taskId}/instance`, { headers })
      ]);
      setTask(taskRes.data);
      
      // Check if already submitted - handle paginated response
      const submissions = submissionsRes.data.items || submissionsRes.data;
      const existing = Array.isArray(submissions) 
        ? submissions.find(s => s.task_id === taskId && s.status !== "rejected")
        : null;
      setExistingSubmission(existing);
      
      // Check for active instance
      if (instanceRes.data.has_active_instance) {
        setActiveInstance(instanceRes.data);
      }
    } catch (error) {
      console.error("Error fetching task:", error);
      toast.error("Failed to load task");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTask = async () => {
    // Check if this is an email task and user has no registered emails
    const isEmailTask = EMAIL_TASK_TYPES.includes(task?.task_type) || 
                        task?.task_type?.toLowerCase().includes('email');
    
    if (isEmailTask && registeredEmails.length === 0) {
      setShowEmailPrompt(true);
      return;
    }

    setStarting(true);
    try {
      const response = await axios.post(`${API}/v2/tasks/${taskId}/start`, {}, { headers });
      toast.success(response.data.message);
      
      if (response.data.emails_sent_count > 0) {
        toast.info(`Emails sent to: ${response.data.emails_sent_to.join(", ")}`);
      }
      
      // Refresh to get the instance
      const instanceRes = await axios.get(`${API}/v2/tasks/${taskId}/instance`, { headers });
      if (instanceRes.data.has_active_instance) {
        setActiveInstance(instanceRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start task");
    } finally {
      setStarting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshot(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (task.completion_method === "manual" && !screenshot) {
      toast.error("Please upload a screenshot");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/v2/submissions`, {
        task_id: taskId,
        screenshot_data: screenshot
      }, { headers });

      if (task.completion_method === "auto") {
        toast.success(`Task completed! You earned ${task.points_reward} points`);
      } else {
        toast.success("Task submitted for review");
      }

      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickUrl = async () => {
    // Open URL in new tab
    if (task.target_url) {
      window.open(task.target_url, "_blank");
    }
    // Auto-complete for click_url tasks
    if (task.completion_method === "auto") {
      await handleSubmit();
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case "click_url": return MousePointerClick;
      case "reply_email": return Mail;
      case "social_post": return Share2;
      case "social_review": return Camera;
      default: return CheckCircle;
    }
  };

  const getTaskColor = (type) => {
    switch (type) {
      case "click_url": return "bg-blue-500";
      case "reply_email": return "bg-green-500";
      case "social_post": return "bg-purple-500";
      case "social_review": return "bg-orange-500";
      default: return "bg-slate-500";
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeProgress = () => {
    if (!activeInstance || !remainingTime) return 0;
    const totalSeconds = (task?.completion_minutes || 10) * 60;
    return ((totalSeconds - remainingTime) / totalSeconds) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-500">Task not found</p>
      </div>
    );
  }

  const TaskIcon = getTaskIcon(task.task_type);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="task-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-slate-900">GigsEasy</h1>
                <p className="text-sm text-slate-500">Task Details</p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button variant="ghost" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {existingSubmission ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Task Already Submitted
              </h2>
              <p className="text-slate-600 mb-4">
                {existingSubmission.status === "pending" 
                  ? "Your submission is pending review"
                  : existingSubmission.status === "approved"
                  ? `You earned ${existingSubmission.points_awarded} points!`
                  : "Your submission was processed"}
              </p>
              <Badge
                className={
                  existingSubmission.status === "approved"
                    ? "bg-green-100 text-green-700"
                    : existingSubmission.status === "pending"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-700"
                }
              >
                {existingSubmission.status}
              </Badge>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Active Timer Banner */}
            {activeInstance && remainingTime > 0 && (
              <Card className="border-2 border-amber-400 bg-amber-50 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Task In Progress</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <span className={`font-heading text-2xl font-bold ${remainingTime < 60 ? 'text-red-600' : 'text-amber-800'}`}>
                        {formatTime(remainingTime)}
                      </span>
                    </div>
                  </div>
                  <Progress value={getTimeProgress()} className="h-2" />
                  <p className="text-sm text-amber-700 mt-2">
                    Complete this task before the timer runs out!
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="border-slate-200 mb-6">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${getTaskColor(task.task_type)} flex items-center justify-center`}>
                    <TaskIcon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="font-heading text-xl">{task.title}</CardTitle>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge variant="outline" className="capitalize">
                        {task.task_type?.replace("_", " ")}
                      </Badge>
                      <span className="font-heading font-bold text-amber-600">
                        {task.points_reward} points
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                    <p className="text-slate-600">{task.description}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Instructions</h3>
                    <div className="bg-slate-50 border border-slate-200 p-4">
                      <p className="text-slate-700 whitespace-pre-wrap">{task.instructions}</p>
                    </div>
                  </div>

                  {task.target_url && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Target URL</h3>
                      <a 
                        href={task.target_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-2"
                      >
                        {task.target_url}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-blue-900 text-sm">Time Limit</span>
                      </div>
                      <p className="text-blue-700 text-sm">{task.completion_minutes || 10} minutes to complete</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-900 text-sm">Email Notification</span>
                      </div>
                      <p className="text-purple-700 text-sm">Sent to all your registered emails</p>
                    </div>
                  </div>

                  {/* Warning for email tasks without registered emails */}
                  {(EMAIL_TASK_TYPES.includes(task.task_type) || task.task_type?.toLowerCase().includes('email')) && 
                   registeredEmails.length === 0 && (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-900 text-sm">
                            Email registration required
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            You need to register at least one email address before you can participate in this task.
                          </p>
                          <Link to="/dashboard">
                            <Button size="sm" variant="outline" className="mt-2 h-8 text-red-700 border-red-300 hover:bg-red-100">
                              <Plus className="w-3 h-3 mr-1" />
                              Add Email Now
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900">
                          {task.completion_method === "auto" 
                            ? "This task will be auto-completed"
                            : "This task requires manual approval"}
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          {task.completion_method === "auto"
                            ? "Points will be credited immediately after completion"
                            : "Upload a screenshot as proof and wait for admin review"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Card */}
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg">
                  {!activeInstance ? "Start Task" : "Complete Task"}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!activeInstance ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 border border-slate-200 p-4">
                      <p className="text-slate-700">
                        When you start this task:
                      </p>
                      <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                        <li>An email will be sent to all your registered email addresses</li>
                        <li>You will have <strong>{task.completion_minutes || 10} minutes</strong> to complete the task</li>
                        <li>Follow the instructions in the email to complete the task</li>
                      </ul>
                    </div>
                    <Button
                      onClick={handleStartTask}
                      disabled={starting}
                      className="w-full h-12"
                      data-testid="start-task-btn"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      {starting ? "Starting..." : "Start Task & Send Emails"}
                    </Button>
                  </div>
                ) : task.task_type === "click_url" && task.completion_method === "auto" ? (
                  <Button
                    onClick={handleClickUrl}
                    disabled={submitting || remainingTime === 0}
                    className="w-full h-12"
                    data-testid="click-url-btn"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    {submitting ? "Completing..." : "Click URL & Complete Task"}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-3">
                        Upload a screenshot as proof of task completion
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="screenshot-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-12"
                        disabled={remainingTime === 0}
                        data-testid="upload-screenshot-btn"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        {screenshot ? "Change Screenshot" : "Upload Screenshot"}
                      </Button>
                    </div>

                    {screenshot && (
                      <div className="border border-slate-200 p-2">
                        <img 
                          src={screenshot} 
                          alt="Screenshot preview" 
                          className="max-h-48 mx-auto object-contain"
                        />
                      </div>
                    )}

                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || !screenshot || remainingTime === 0}
                      className="w-full h-12"
                      data-testid="submit-task-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      {submitting ? "Submitting..." : "Submit for Review"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Email Registration Prompt Dialog */}
      <Dialog open={showEmailPrompt} onOpenChange={setShowEmailPrompt}>
        <DialogContent className="mx-4 sm:mx-auto max-w-[calc(100%-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5 text-primary" />
              Email Required
            </DialogTitle>
            <DialogDescription className="text-sm pt-2">
              This task requires you to have at least one registered email address to participate.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">No emails registered</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Please add an email address from your dashboard to receive task notifications and participate in email tasks.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11"
                onClick={() => setShowEmailPrompt(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11"
                onClick={() => navigate("/dashboard")}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
